import { Injectable } from '@angular/core';
import { map, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ApiEndpoints } from '../constants/api-endpoints';
import { AuthResponse } from '../models/auth-response';
import { ApiService } from './api.service';
import { TokenStorageService } from './token-storage.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(private readonly api: ApiService, private readonly tokens: TokenStorageService) {}

  login(credentials: { email: string; password: string; rememberSession?: boolean }) {
    const { rememberSession = false, ...payload } = credentials;

    return this.api.postHandler(ApiEndpoints.AUTH_LOGIN, payload, { isAuthNeeded: false }).pipe(
      map((response) => this.toAuthResponse(response)),
      tap((response) => this.tokens.saveTokens(response, { persistent: rememberSession }))
    );
  }

  refresh() {
    const refreshToken = this.tokens.refreshToken;
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    return this.api.postHandler(ApiEndpoints.AUTH_REFRESH, { refreshToken }, { isAuthNeeded: false }).pipe(
      map((response) => this.toAuthResponse(response)),
      tap((response) => this.tokens.saveTokens(response, { persistent: this.tokens.prefersPersistentSession }))
    );
  }

  logout(): void {
    this.tokens.clear();
  }

  requestRegistrationOtp(payload: { email: string }) {
    return this.api.postHandler(
      ApiEndpoints.AUTH_REGISTER_OTP,
      { email: payload.email },
      this.publicAuthOptions()
    );
  }

  requestPasswordResetOtp(payload: { email: string }) {
    return this.api.postHandler(
      ApiEndpoints.AUTH_PASSWORD_RESET_OTP,
      { email: payload.email },
      this.publicAuthOptions()
    );
  }

  resetPassword(payload: { email: string; otp: string; newPassword: string }) {
    return this.api.postHandler(
      ApiEndpoints.AUTH_PASSWORD_RESET,
      {
        email: payload.email,
        otp: payload.otp,
        newPassword: payload.newPassword
      },
      this.publicAuthOptions()
    );
  }

  register(payload: {
    name: string;
    organizationName?: string | null;
    mobileNumber?: string | null;
    email: string;
    password: string;
    otp: string;
  }) {
    return this.api.postHandler(
      ApiEndpoints.AUTH_REGISTER,
      {
        name: payload.name,
        organizationName: payload.organizationName ?? null,
        mobileNumber: payload.mobileNumber ?? null,
        email: payload.email,
        password: payload.password,
        accountId: null,
        otp: payload.otp
      },
      this.publicAuthOptions()
    );
  }

  private toAuthResponse(response: any): AuthResponse {
    return {
      token: `${response?.token ?? ''}`,
      refreshToken: `${response?.refreshToken ?? ''}`,
      expiresIn: Number(response?.expiresIn ?? 0),
      refreshExpiresIn: Number(response?.refreshExpiresIn ?? 0)
    };
  }

  private publicAuthOptions() {
    return {
      headers: this.publicAuthHeaders(),
      isAuthNeeded: false
    };
  }

  private publicAuthHeaders(): { [header: string]: string } {
    return {
      Auth: 'a',
      Test: '1',
      tracerId: `web-${Date.now()}`
    };
  }
}
